/** Client-side mirror of the API shapes. Kept independent of the backend packages so the SPA builds in isolation. */

export type RunStatus =
  | "queued"
  | "blocked"
  | "launching"
  | "running"
  | "waiting_for_input"
  | "completed"
  | "failed"
  | "cancelled"
  | "expired";

export type TaskStatus =
  | "open"
  | "blocked"
  | "waiting_for_approval"
  | "waiting_for_human"
  | "completed"
  | "failed"
  | "cancelled";
export type MergeStatus =
  | "waiting"
  | "ready"
  | "approved"
  | "held"
  | "merging"
  | "merged"
  | "failed"
  | "cancelled";
/** Lane ids come from the factory config, e.g. "standard" and "hotfix". */
export type LaneId = string;

export interface IssueSnapshot {
  id: string;
  identifier: string;
  title: string;
  description: string;
  url: string;
  state: string;
  priority: number | null;
  labels: string[];
  assignee: string | null;
  comments: Array<{ author: string; body: string; createdAt: string }>;
  /** Issue type key, e.g. "bug". Present on a full task, omitted from floor snapshots. */
  type?: string | null;
}

export interface Task {
  id: string;
  trackerProvider: string;
  trackerIssueId: string | null;
  issueIdentifier: string | null;
  issueUrl: string | null;
  title: string;
  description: string;
  repositoryUrl: string;
  baseRef: string;
  /** Null while the work order is in intake, and for detached runs. */
  lane: LaneId | null;
  status: TaskStatus;
  currentAgentId: string | null;
  currentRunId: string | null;
  issueSnapshot: IssueSnapshot | null;
  /** Type key of the linked issue. Floor snapshots set this; a live task also has `issueSnapshot.type`. */
  issueTypeKey?: string | null;
  instructions: string | null;
  meta: Record<string, unknown>;
  createdBy: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What a work order has spent across every station run. Each total is null until some run reports it. */
export interface TaskSpend {
  costUsd: number | null;
  totalTokens: number | null;
  /** Summed start-to-finish time of every run, so parallel stations add up. A live run counts up to now. */
  agentMs: number | null;
  /** Runs with token counts but no cost yet: the model cost can land minutes after the tokens. */
  unbilled: number;
}

/** Why a work order is waiting on a person, when the engine held it rather than a reviewer. */
export type HoldReason =
  | { kind: "loop_budget"; used: number; allowed: number }
  | { kind: "no_progress"; headSha: string };

export interface LoopGrant {
  id: string;
  taskId: string;
  amount: number;
  /** Display name of the admin who added the loops. */
  grantedBy: string;
  reason: string | null;
  createdAt: string;
}

/** Verifier → Fixer loops on one work order: the factory default plus admin grants, against the loops spent. */
export interface LoopBudget {
  included: number;
  granted: number;
  allowed: number;
  used: number;
  remaining: number;
  /** Most loops one grant can add. */
  maxGrant: number;
  /** Most loops one work order can ever be allowed. */
  ceiling: number;
  grants: LoopGrant[];
}

/** A row of `/tasks`: the slim task plus its spend. */
export type TaskListItem = Task & { spend: TaskSpend };

export type TaskSort = "created" | "cost" | "tokens" | "agentTime";

export interface HandoffContext {
  fromAgentId: string;
  fromRunId: string;
  summary: string;
  verdict?: string;
  prUrl?: string;
  branch?: string;
  commitSha?: string;
  testResults?: string;
  findings?: string[];
  classification?: string;
  acceptanceCriteria?: string[];
  estimate?: {
    storyPoints: number;
    developmentImpact: string;
    businessImpact: string;
    testingImpact: string;
    sla: string;
    dueAt: string;
  };
  lane?: LaneId;
}

export interface Run {
  id: string;
  taskId: string;
  agentId: string;
  provider: string;
  attempt: number;
  status: RunStatus;
  providerAgentId: string | null;
  providerRunId: string | null;
  providerMeta: Record<string, unknown>;
  prompt?: string | null;
  resultText: string | null;
  structured: Record<string, unknown> | null;
  verdict: string | null;
  summary: string | null;
  error: string | null;
  blockedReason: string | null;
  usage: {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    costUsd?: number;
    durationMs?: number;
  } | null;
  mcpServers: string[];
  handoff: HandoffContext | null;
  deadlineAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * What `/tasks/:id` sends for every run in its history: everything but
 * `resultText`, `structured` and `handoff`, which are tens of kB each and
 * only ever shown for one run at a time (`currentRun`, or a `Run` row once
 * expanded fetches its own detail with `api.run`).
 */
export type RunListItem = Omit<Run, "resultText" | "structured" | "handoff">;

export interface RunEvent {
  id: number;
  runId: string;
  seq: number;
  type: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface Artifact {
  id: string;
  taskId: string;
  runId: string | null;
  type: "pull_request" | "branch" | "commit" | "summary" | "file" | "release" | "plan" | "issue";
  label: string;
  url: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface MergeQueueItem {
  id: string;
  taskId: string;
  runId: string | null;
  repositoryUrl: string;
  prUrl: string;
  prNumber: number;
  headBranch: string;
  baseBranch: string;
  lane: LaneId;
  status: MergeStatus;
  position: number;
  checks: { ci: string; review: string; mergeable: boolean | null; conflicts: boolean; draft?: boolean };
  blockers: string[];
  requiresApproval: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  mergedSha: string | null;
  releaseTag: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentSummary {
  id: string;
  name: string;
  description: string;
  section: string;
  provider: string;
  model?: string;
  modelParams?: Array<{ id: string; value: string }>;
  mode: "agent" | "plan";
  kind: "llm" | "policy";
  policy?: string;
  output: string;
  session: string;
  autoCreatePr: boolean;
  /** Settings repo key a detached specialist works in when started without one. */
  repoKey?: string;
  mcp: { required: string[]; optional: string[] };
  triggers: Array<Record<string, unknown> & { type: string }>;
  routing: {
    onSuccess: { next: string; trackerState?: string; comment: boolean };
    onFailure: { next: string; trackerState?: string; comment: boolean };
    onVerdict: Record<string, { next: string; trackerState?: string; comment: boolean }>;
  };
  tracker: { stateOnStart?: string; stateOnSuccess?: string; stateOnFailure?: string };
  timeoutMinutes?: number;
  station: { icon: string; color: string; activity: string };
}

export interface Section {
  id: string;
  name: string;
  description: string;
  color: string;
  mcpDefaults: string[];
  /** Off the line: its agents file tracker issues instead of handing work to a station. */
  detached: boolean;
  agents: AgentSummary[];
}

export interface Lane {
  name: string;
  description: string;
  color: string;
  stations: string[];
}

export interface McpServerSummary {
  id: string;
  name: string;
  description: string;
  transport: "http" | "sse" | "stdio";
  url?: string;
  command?: string;
  tags: string[];
  docsUrl?: string;
  icon: string;
  auth: { type: "none" | "bearer" | "header" | "oauth" };
  defaultAllowedTools: string[];
}

export interface FactoryLayout {
  name: string;
  repository: {
    url: string;
    defaultBranch: string;
    requireHumanApproval: boolean;
    mergeMethod: string;
    deleteBranchOnMerge: boolean;
    deployWorkflow?: string;
  };
  tracker: {
    provider: string;
    optInLabel: string;
  };
  sections: Section[];
  /** Stations every work order passes before its lane. */
  intake: string[];
  lanes: Record<LaneId, Lane>;
  mcpServers: McpServerSummary[];
}

export interface FactoryState {
  generatedAt: string;
  tasks: Task[];
  runs: Run[];
  mergeQueue: MergeQueueItem[];
  kpis: {
    inProduction: number;
    runningAgents: number;
    queued: number;
    blocked: number;
    waitingForApproval: number;
    waitingForHuman: number;
    completedLast24h: number;
    failedLast24h: number;
    mergedLast24h: number;
  };
  stations: Record<string, { active: number; queued: number; blocked: number }>;
  providers: Record<string, { configured: boolean; capabilities: Record<string, unknown> }>;
  tracker: { id: string; configured: boolean };
  scm: { id: string; configured: boolean };
}

export interface McpServerView {
  server: McpServerSummary;
  connection: {
    enabled: boolean;
    authStatus: "disconnected" | "connected" | "expired" | "error";
    tokenExpiresAt: string | null;
    allowedTools: string[] | null;
    attachments: { sections: string[]; agents: string[] };
    discoveredTools: Array<{ name: string; description?: string }>;
    lastHealth: { ok: boolean; checkedAt: string; error?: string; toolCount?: number } | null;
    lastError: string | null;
    updatedAt: string | null;
    updatedBy: string | null;
  };
  usedBy: Array<{ agentId: string; required: boolean }>;
}

export type RealtimeEvent =
  | { type: "task.updated"; task: Task }
  | { type: "run.updated"; run: Run }
  | {
      type: "run.event";
      runId: string;
      taskId: string;
      event: { seq: number; type: string; payload: Record<string, unknown>; createdAt: string };
    }
  | { type: "merge.updated"; item: MergeQueueItem }
  | { type: "issue.updated"; issueId: string; identifier: string }
  | { type: "issue.comment"; issueId: string; identifier: string }
  | { type: "preview.updated"; preview: Preview; issueId: string | null; identifier: string | null }
  | { type: "release.updated"; release: ReleaseView; issueIds: string[] }
  | { type: "factory.notice"; level: "info" | "warning" | "error"; message: string };

// ------------------------------------------------------------- releases

export type ReleaseKind = "standard" | "urgent" | "scheduled" | "manual";
export type ReleaseStatus =
  | "open"
  | "shipping"
  | "published"
  | "deploying"
  | "deployed"
  | "failed"
  | "cancelled";

export const OPEN_RELEASE: ReadonlySet<ReleaseStatus> = new Set(["open", "shipping"]);

/** A release as returned by /releases. GitHub is the system of record; this mirrors it plus Skynet's plan. */
export interface ReleaseView {
  id: string;
  repositoryUrl: string;
  kind: ReleaseKind;
  status: ReleaseStatus;
  source: "skynet" | "github";
  branch: string;
  baseTag: string | null;
  fromLastRelease: boolean;
  scheduledFor: string | null;
  githubReleaseId: number | null;
  githubUrl: string | null;
  name: string;
  tag: string | null;
  tagLocked: boolean;
  targetCommitish: string | null;
  targetSha: string | null;
  draft: boolean;
  prerelease: boolean;
  publishedAt: string | null;
  githubAuthor: string | null;
  body: string;
  /** Why GitHub's generated notes are missing from the body. */
  notesError: string | null;
  /** Why Skynet stopped part way through shipping a release GitHub already published. */
  finishError: string | null;
  /** Changes in the tag's range that Skynet did not build; null until the range has been read. */
  untrackedCount: number | null;
  deployUrl: string | null;
  deployStartedAt: string | null;
  deployedAt: string | null;
  syncSha: string | null;
  syncConflict: boolean;
  error: string | null;
  createdBy: string | null;
  shippedBy: string | null;
  shippedAt: string | null;
  createdAt: string;
  updatedAt: string;
  issueCount: number;
}

export interface ReleaseEventView {
  seq: number;
  type: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface LatestRelease {
  tag: string;
  name: string;
  url: string;
  publishedAt: string | null;
}

export interface ReleaseChangePullRequest {
  number: number;
  url: string;
  title: string;
  author: string | null;
  issue: { id: string; identifier: string; releaseId: string | null } | null;
}

export interface ReleaseChangeCommit {
  sha: string;
  title: string;
  author: string | null;
  url: string;
}

/** Pull requests and commits between the previous tag and the release's commit. */
export interface ReleaseChanges {
  base: string;
  head: string;
  compareUrl: string;
  checkedAt: string;
  partial: boolean;
  pullRequests: ReleaseChangePullRequest[];
  commits: ReleaseChangeCommit[];
}

export interface ReleaseDetail {
  release: ReleaseView;
  issues: IssueView[];
  merges: Record<string, { status: MergeStatus; prUrl: string; blockers: string[] }>;
  events: ReleaseEventView[];
  changes: ReleaseChanges | null;
  suggestedTag: string | null;
  latest: LatestRelease | null;
}

/** The release an issue ships in, embedded in IssueView. */
export interface ReleaseSummary {
  id: string;
  kind: ReleaseKind;
  status: ReleaseStatus;
  name: string;
  tag: string | null;
  githubUrl: string | null;
  scheduledFor: string | null;
  branch: string;
  publishedAt: string | null;
}

export type ReleaseTarget =
  | { kind: "standard" }
  | { kind: "urgent"; fromLastRelease?: boolean }
  | { kind: "scheduled"; scheduledFor: string }
  | { kind: "existing"; releaseId: string }
  | { kind: "none" };

// ------------------------------------------------------------- previews

export type PreviewStatus =
  | "requested"
  | "provisioning"
  | "ready"
  | "updating"
  | "stopped"
  | "expired"
  | "failed";

export const ACTIVE_PREVIEW: ReadonlySet<PreviewStatus> = new Set([
  "requested",
  "provisioning",
  "ready",
  "updating",
]);

export interface PreviewApps {
  admin?: string;
  client?: string;
  employer?: string;
}

/** Full preview row, as returned by /previews. */
export interface Preview {
  id: string;
  slug: string;
  taskId: string | null;
  issueId: string | null;
  repositoryUrl: string;
  prNumber: number;
  prUrl: string;
  headBranch: string | null;
  headSha: string | null;
  requestedSha: string | null;
  generation: number;
  status: PreviewStatus;
  phase: string | null;
  statusMessage: string | null;
  lastError: string | null;
  apps: PreviewApps | null;
  ecsTaskArn: string | null;
  privateIp: string | null;
  prCommentId: string | null;
  ttlMinutes: number;
  expiresAt: string | null;
  launchedAt: string | null;
  readyAt: string | null;
  stoppedAt: string | null;
  stopReason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PreviewEvent {
  previewId: string;
  seq: number;
  type: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

/** Issue-facing view of the newest preview, embedded in IssueDetail. */
export interface PreviewSummary {
  id: string;
  slug: string;
  status: PreviewStatus;
  phase: string | null;
  statusMessage: string | null;
  generation: number;
  headSha: string | null;
  prUrl: string;
  apps: PreviewApps | null;
  expiresAt: string | null;
  readyAt: string | null;
  stoppedAt: string | null;
  stopReason: string | null;
  lastError: string | null;
  canRetry: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuditEntry {
  id: number;
  actor: string;
  action: string;
  target: string;
  detail: Record<string, unknown>;
  createdAt: string;
}

export const ACTIVE_RUN: ReadonlySet<RunStatus> = new Set(["launching", "running", "waiting_for_input"]);
export const TERMINAL_RUN: ReadonlySet<RunStatus> = new Set(["completed", "failed", "cancelled", "expired"]);

// ---------------------------------------------------------------- issues

export type StatusCategory = "backlog" | "unstarted" | "started" | "completed" | "cancelled";
export type SlaStatus = "none" | "on_track" | "at_risk" | "breached" | "met";

/** Why an issue waits on a person. Mirrors `WAITING_ON` in `@skynet/core`, in group order. */
export const WAITING_ON = ["signoff", "held", "input", "draft", "review", "stalled"] as const;
export type WaitingOn = (typeof WAITING_ON)[number];
export type RelationType = "duplicate" | "related" | "blocks";

export interface UserBadgeInfo {
  id: string;
  displayName: string;
  email: string;
  avatarUrl: string | null;
  kind: "human" | "system";
  active: boolean;
}

export interface TeamBadgeInfo {
  id: string;
  key: string;
  name: string;
  color: string;
  icon: string;
}

export interface LabelInfo {
  id: string;
  name: string;
  color: string;
  teamId: string | null;
}

export interface IssueStatusInfo {
  key: string;
  name: string;
  color: string;
  category: StatusCategory;
  position: number;
}

export interface IssueTypeInfo {
  key: string;
  name: string;
  identifierPrefix: string;
  icon: string;
  color: string;
  position: number;
}

export interface SlaPolicyInfo {
  id: string;
  name: string;
  issueTypeKey: string | null;
  priority: number | null;
  sla: string;
}

/** A repository the factory works on, managed from Settings. */
export interface RepoInfo {
  key: string;
  name: string;
  url: string;
  defaultBranch: string;
  /** Inactive repos keep their issues but cannot be picked for new ones. */
  active: boolean;
  /** New issues land here when no repo is named. Exactly one active repo. */
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RepoView extends RepoInfo {
  issueCount: number;
}

export interface IssueRefInfo {
  id: string;
  identifier: string;
  title: string;
  statusKey: string;
}

export interface IssueView {
  id: string;
  number: number;
  identifier: string;
  title: string;
  typeKey: string;
  statusKey: string;
  /** The repo the work lands in. */
  repoKey: string;
  priority: number;
  storyPoints: number | null;
  prUrl: string | null;
  /** Whether business testing needs a running preview. Null until decided. */
  previewRequired: boolean | null;
  release: ReleaseSummary | null;
  sla: string | null;
  slaDueAt: string | null;
  slaStatus: SlaStatus;
  source: "web" | "mcp" | "api" | "agent" | "jira";
  parentId: string | null;
  taskId: string | null;
  currentAgentId: string | null;
  waitingOn: WaitingOn | null;
  reporter: UserBadgeInfo | null;
  reviewer: UserBadgeInfo | null;
  teams: TeamBadgeInfo[];
  labels: LabelInfo[];
  parent: IssueRefInfo | null;
  subIssues: { total: number; done: number };
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IssueRelationView {
  type: RelationType;
  direction: "outbound" | "inbound";
  issue: IssueRefInfo;
}

export interface IssueAttachment {
  id: string;
  issueId: string;
  commentId: string | null;
  uploaderId: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  storageKey: string;
  status: "pending" | "uploaded";
  /** Set when the file is evidence for a test step rather than a general attachment. */
  testStepId: string | null;
  caption: string | null;
  createdAt: string;
}

export type TestStepApp = "admin" | "client" | "employer";
export type TestStepOutcome = "worked" | "failed";

export interface IssueTestStep {
  id: string;
  issueId: string;
  position: number;
  app: TestStepApp | null;
  title: string;
  /** Markdown: where to go and what to do. */
  description: string;
  /** Markdown: what the tester should see. */
  expected: string;
  authorId: string | null;
  agentId: string | null;
  createdAt: string;
  attachments: IssueAttachment[];
}

export interface TestStepResult {
  stepId: string;
  userId: string;
  result: TestStepOutcome;
  note: string;
  updatedAt: string;
}

export interface IssueDetail extends IssueView {
  description: string;
  testingInstructions: string;
  externalRef: Record<string, unknown> | null;
  relations: IssueRelationView[];
  /** Every uploaded file; step evidence has `testStepId` set. */
  attachments: IssueAttachment[];
  testSteps: IssueTestStep[];
  /** The signed-in person's outcomes, keyed by step id. */
  testStepResults?: Record<string, TestStepResult>;
  children: IssueView[];
  /** The newest preview environment for this issue, in any state. */
  preview?: PreviewSummary | null;
  /** Business sign-off for the signed-in person. Null unless the issue is in business testing. */
  signOff?: SignOffState | null;
}

export interface SignOffState {
  /** The signed-in person may sign off or request changes now. */
  canAct: boolean;
  /** Why they cannot act. Null when they can, and once the issue is signed off. */
  reason: string | null;
  /** Display name of whoever signed off. */
  approvedBy: string | null;
  approvedAt: string | null;
}

export interface IssueComment {
  id: string;
  issueId: string;
  authorId: string;
  agentId: string | null;
  body: string;
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
  author: UserBadgeInfo | null;
  attachments: IssueAttachment[];
}

export type InboxReason = "mention" | "reporter" | "reviewer";

export interface InboxComment {
  id: string;
  body: string;
  createdAt: string;
  author: UserBadgeInfo | null;
  issue: { identifier: string; title: string };
  reasons: InboxReason[];
}

export interface InboxCommentsPage {
  comments: InboxComment[];
  total: number;
}

export interface InboxResponse {
  comments: InboxComment[];
  issues: Array<{
    identifier: string;
    title: string;
    statusKey: string;
    typeKey: string;
    priority: number;
    updatedAt: string;
    roles: Array<"reporter" | "reviewer">;
  }>;
}

export interface IssueActivity {
  id: number;
  issueId: string;
  actorId: string | null;
  agentId: string | null;
  kind: string;
  field: string | null;
  fromValue: unknown;
  toValue: unknown;
  createdAt: string;
  actor: UserBadgeInfo | null;
}

export interface IssuePage {
  items: IssueView[];
  total: number;
  groups?: Array<{ key: string; count: number }>;
}

export interface IssuesMeta {
  statuses: IssueStatusInfo[];
  types: IssueTypeInfo[];
  labels: LabelInfo[];
  teams: Array<TeamBadgeInfo & { description: string; memberCount: number; slackChannel: string | null }>;
  repos: RepoInfo[];
  users: UserBadgeInfo[];
  slaPolicies: SlaPolicyInfo[];
}

export interface TeamView extends TeamBadgeInfo {
  description: string;
  slackChannel: string | null;
  createdAt: string;
  updatedAt: string;
  members: Array<UserBadgeInfo & { role: "approver" | "member" }>;
  issueCount: number;
}

/**
 * Admins can do everything. Managers edit and route issues, sign off any issue, manage
 * teams, and place issues in releases, but cannot change settings or skip review. Users can
 * create issues and see teams and analytics, and approvers among them can sign off their
 * teams' issues in business testing. Viewers can only read issues and comment.
 */
export type UserRole = "admin" | "manager" | "user" | "viewer";

export interface DirectoryUser extends UserBadgeInfo {
  entraId: string | null;
  leaselabId: string | null;
  /** Slack member id (`U…`) from the directory sync. Null until the person's email matches Slack. */
  slackUserId: string | null;
  jobTitle: string | null;
  department: string | null;
  source: string;
  role: UserRole;
  /** A Cognito login is linked: the person has signed in to Skynet. */
  hasAccount: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApiKeyInfo {
  id: string;
  name: string;
  prefix: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface IssueSettings {
  defaultTeamId: string | null;
  slaAtRiskRatio: number;
}

export interface WeeklyAnalyticsRow {
  weekStart: string;
  typeKey: string;
  created: number;
  completed: number;
  cancelled: number;
  openAtEnd: number;
  storyPoints: number;
  medianCycleHours: number | null;
  p85CycleHours: number | null;
  medianLeadHours: number | null;
  slaMet: number;
  slaTotal: number;
  slaBreached: number;
  inFactory: number;
}

/** Counts and agent cost for one issue type over the selected analytics range. */
export interface TypePeriodRow {
  typeKey: string;
  created: number;
  completed: number;
  storyPoints: number;
  runs: number;
  costUsd: number;
}

export interface SnapshotRow {
  statusKey: string;
  typeKey: string;
  priority: number;
  count: number;
}

/** Lifetime cost, run time and session volume for one factory agent type. */
export interface AgentCostRow {
  agentId: string;
  name: string;
  color: string;
  sessions: number;
  totalCostUsd: number;
  avgRunMinutes: number | null;
}

export type SearchKind = "issues" | "tasks" | "teams" | "people" | "labels" | "releases";

/** `GET /search`. Kinds the caller's role may not list come back empty and are left out of `kinds`. */
export interface SearchResults {
  query: string;
  kinds: SearchKind[];
  issues: Array<{
    id: string;
    identifier: string;
    title: string;
    typeKey: string;
    statusKey: string;
    priority: number;
    parent: { identifier: string; title: string } | null;
    teams: TeamBadgeInfo[];
    archived: boolean;
    /** The imported Jira key, when the issue matched a key-shaped query by that key rather than its own. */
    jiraKey: string | null;
  }>;
  tasks: Array<{
    id: string;
    title: string;
    status: TaskStatus;
    issueIdentifier: string | null;
    currentAgentId: string | null;
    lane: string | null;
    updatedAt: string;
  }>;
  teams: Array<TeamBadgeInfo & { description: string }>;
  people: Array<UserBadgeInfo & { teams: TeamBadgeInfo[] }>;
  labels: LabelInfo[];
  releases: Array<{
    id: string;
    name: string;
    tag: string | null;
    kind: ReleaseKind;
    status: ReleaseStatus;
    updatedAt: string;
  }>;
}

export const PRIORITIES: Array<{ value: number; label: string }> = [
  { value: 0, label: "No priority" },
  { value: 1, label: "Urgent" },
  { value: 2, label: "High" },
  { value: 3, label: "Medium" },
  { value: 4, label: "Low" },
];
